import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { length } from '@turf/length';
import { FeatureProperties, MapModes } from '../constants';
import { createCircleFromPoint, getRadiusDescription } from '../utils';
import { buildCircleFeature } from './draw-circle';

const DrawCircleSelect = { ...MapboxDraw.modes.direct_select };

class CircleSelectState {
    dragMoveLocation: any;
    boxSelectStartLocation: any;
    boxSelectElement: any;
    boxSelecting: boolean;
    canBoxSelect: boolean;
    dragMoving: boolean;
    canDragMove: boolean;
    featureId: string;
    selectedCoordPaths: any[];
    feature: any; // DrawFeature for the spoke

    polygon: any; // DrawFeature for the ring
}

DrawCircleSelect.onSetup = function (opts: any) {
    const state: CircleSelectState = {
        dragMoveLocation: null,
        boxSelectStartLocation: null,
        boxSelectElement: undefined,
        boxSelecting: false,
        canBoxSelect: false,
        dragMoving: false,
        canDragMove: true,
        featureId: opts.featureId,
        polygon: null,
        feature: null,
        selectedCoordPaths: opts.coordPath ? [opts.coordPath] : []
    };

    state.feature = this.getFeature(opts.featureId);

    state.polygon = this.newFeature({
        type: 'Feature',
        properties: {
            meta: FeatureProperties.CIRCLE_POLYGON,
            customDrawMode: MapModes.DRAW_CIRCLE_SELECT,
            parent: state.feature.id
        },
        geometry: {
            type: 'Polygon',
            coordinates: []
        }
    });

    // @ts-ignore: not in the current type
    this.updateCircle(state);
    this.addFeature(state.polygon);

    this.setSelected([state.feature.id]);
    this.setSelectedCoordinates(
        // @ts-ignore: not in the current type
        this.pathsToCoordinates(state.feature.id, state.selectedCoordPaths)
    );

    this.setActionableState({
        trash: true,
        combineFeatures: false,
        uncombineFeatures: false
    });

    return state;
};

// @ts-ignore: not in the current type
DrawCircleSelect.updateCircle = function (state: CircleSelectState) {
    const centre = state.feature.coordinates[0];
    const radiusMetres = state.feature.properties.radiusMetres;

    const ring = createCircleFromPoint(centre, radiusMetres);
    state.polygon.incomingCoords(ring.geometry.coordinates);
    state.feature.properties.measurement = getRadiusDescription(radiusMetres);
};

const oldToDisplayFeatures = DrawCircleSelect.toDisplayFeatures;
DrawCircleSelect.toDisplayFeatures = function (
    state: CircleSelectState,
    geojson: GeoJSON.Feature,
    display: (feature: GeoJSON.GeoJSON) => void
) {
    oldToDisplayFeatures.call(
        this,
        state,
        geojson,
        (feature: GeoJSON.Feature) => {
            if (
                feature.properties?.meta === MapboxDraw.constants.meta.MIDPOINT
            ) {
                return;
            }
            display(feature);
        }
    );
};

// @ts-ignore: not in the current type
const oldDragVertex = DrawCircleSelect.dragVertex;
// @ts-ignore: not in the current type
DrawCircleSelect.dragVertex = function (
    state: CircleSelectState,
    e: any,
    delta: any
) {
    const coordPath = state.selectedCoordPaths[0];
    if (coordPath === '0') {
        MapboxDraw.lib.moveFeatures([state.feature], delta);
        // @ts-ignore: not in the current type
        this.updateCircle(state);
        return;
    }

    oldDragVertex(state, e, delta);

    const lineGeoJson = state.feature.toGeoJSON();
    const km = length(lineGeoJson);
    const radiusMetres = Math.max(1, Math.round(km * 1000));

    state.feature.properties.radiusMetres = radiusMetres;
    // @ts-ignore: not in the current type
    this.updateCircle(state);
};

// @ts-ignore: not in the current type
DrawCircleSelect.dragFeature = function (
    state: CircleSelectState,
    e: any,
    delta: any
) {
    MapboxDraw.lib.moveFeatures(this.getSelected(), delta);
    state.dragMoveLocation = e.lngLat;
    // @ts-ignore: not in the current type
    this.updateCircle(state);
};

/** Whether the event landed on the display-only ring accompanying the spoke */
function isCompanionRing(state: CircleSelectState, e: any): boolean {
    return (
        state.polygon != null &&
        e.featureTarget?.properties?.id === state.polygon.id
    );
}

// The ring is not the selected feature, so the inherited handlers treat it
// as an outsider; route it as the feature body instead, so the circle's area
// responds like a polygon's

const oldOnMouseMove = DrawCircleSelect.onMouseMove;
DrawCircleSelect.onMouseMove = function (state: CircleSelectState, e: any) {
    const result = oldOnMouseMove.call(this, state, e);
    if (isCompanionRing(state, e) && state.selectedCoordPaths.length === 0) {
        this.updateUIClasses({ mouse: MapboxDraw.constants.cursors.MOVE });
    }
    return result;
};

const oldOnMouseDown = DrawCircleSelect.onMouseDown;
DrawCircleSelect.onTouchStart = DrawCircleSelect.onMouseDown = function (
    state: CircleSelectState,
    e: any
) {
    if (isCompanionRing(state, e)) {
        // @ts-ignore: not in the current type
        return this.onFeature(state, e);
    }
    return oldOnMouseDown.call(this, state, e);
};

const oldOnClick = DrawCircleSelect.onClick;
DrawCircleSelect.onClick = function (state: CircleSelectState, e: any) {
    if (isCompanionRing(state, e)) {
        // @ts-ignore: not in the current type
        return this.clickActiveFeature(state, e);
    }
    return oldOnClick.call(this, state, e);
};

const oldOnTap = DrawCircleSelect.onTap;
DrawCircleSelect.onTap = function (state: CircleSelectState, e: any) {
    if (isCompanionRing(state, e)) {
        // @ts-ignore: not in the current type
        return this.clickActiveFeature(state, e);
    }
    return oldOnTap.call(this, state, e);
};

// @ts-ignore: not in the current type
DrawCircleSelect.fireUpdate = function () {
    const selected = this.getSelected()[0];
    if (selected == null) {
        return;
    }
    const spoke = selected.toGeoJSON();
    this.map.fire(MapboxDraw.constants.events.UPDATE, {
        features: [
            buildCircleFeature(
                { type: 'Point', coordinates: spoke.geometry.coordinates[0] },
                { radiusMetres: spoke.properties.radiusMetres }
            )
        ]
    });
};

export default DrawCircleSelect;
